import type {
  EstablishedAhpPlan,
  EstablishedAhpPlanningState,
  EstablishedAhpSessionPlanning,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import {Button, Heading, Text} from '@primer/react'
import {useCallback, useState, type ReactNode} from 'react'

import {useOptionalEstablishedAhpSession} from '../hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '../hooks/use-established-ahp-session-view'
import {getEstablishedAhpSessionIntegrations} from '../hooks/use-established-ahp-session-integrations'

export interface EstablishedAhpSessionPlanProps {
  plan?: EstablishedAhpPlan
  planning?: EstablishedAhpSessionPlanning
  heading?: ReactNode
  emptyState?: ReactNode
  revisionPrompt?: PreparedPrompt
  createRevisionPrompt?: (plan: EstablishedAhpPlan) => PreparedPrompt | undefined
  renderStep?: (step: string, index: number) => ReactNode
  onActionError?: (error: unknown) => void
}

function statusLabel(status: EstablishedAhpPlanningState['status']): string {
  switch (status) {
    case 'pending':
      return 'Awaiting approval'
    case 'approved':
      return 'Approved'
    case 'rejected':
      return 'Rejected'
    case 'failed':
      return 'Action failed'
    default:
      return 'No decision yet'
  }
}

const renderPlanStep = (step: string) => step

/**
 * Optional presentation for the planning contract. The host supplies the
 * policy adapter and, when revision input is meaningful, a typed prompt
 * adapter; this component does not infer AHP plan wire messages.
 */
export function EstablishedAhpSessionPlan({
  plan: suppliedPlan,
  planning: suppliedPlanning,
  heading = 'Plan',
  emptyState = null,
  revisionPrompt,
  createRevisionPrompt,
  renderStep = renderPlanStep,
  onActionError,
}: EstablishedAhpSessionPlanProps) {
  const session = useOptionalEstablishedAhpSession()
  const integrations = session ? getEstablishedAhpSessionIntegrations(session) : undefined
  const planning = suppliedPlanning ?? integrations?.planning
  const plan = suppliedPlan ?? planning?.state.plan
  const view = useOptionalEstablishedAhpSessionViewState()
  const [pendingAction, setPendingAction] = useState<string>()
  const isReadOnly = view.passive

  const run = useCallback(
    async (action: () => Promise<void>, name: string) => {
      if (pendingAction) return
      setPendingAction(name)
      try {
        await action()
      } catch (error) {
        onActionError?.(error)
      } finally {
        setPendingAction(undefined)
      }
    },
    [onActionError, pendingAction],
  )

  if (!plan) return emptyState

  const state = planning?.state ?? {status: 'idle' as const}
  const nextRevisionPrompt = revisionPrompt ?? createRevisionPrompt?.(plan)

  return (
    <section aria-label="Established AHP session plan">
      <Heading as="h2">{heading}</Heading>
      <Text as="p">{statusLabel(state.status)}</Text>
      {isReadOnly ? <Text as="p">This session is open in read-only mode.</Text> : null}
      {plan.title ? <Text as="p">{plan.title}</Text> : null}
      {plan.summary ? <Text as="p">{plan.summary}</Text> : null}
      <ol>
        {plan.steps.map((step, index) => (
          <li key={`${plan.id}-${step}`}>{renderStep(step, index)}</li>
        ))}
      </ol>
      {planning ? (
        <div>
          <Button
            type="button"
            disabled={isReadOnly || pendingAction !== undefined}
            loading={pendingAction === 'approve'}
            onClick={() => void run(planning.actions.approve, 'approve')}
          >
            Approve
          </Button>
          <Button
            type="button"
            disabled={isReadOnly || pendingAction !== undefined}
            loading={pendingAction === 'reject'}
            onClick={() => void run(planning.actions.reject, 'reject')}
          >
            Reject
          </Button>
          <Button
            type="button"
            disabled={isReadOnly || pendingAction !== undefined}
            loading={pendingAction === 'continue'}
            onClick={() => void run(planning.actions.continue, 'continue')}
          >
            Continue
          </Button>
          {nextRevisionPrompt ? (
            <Button
              type="button"
              disabled={isReadOnly || pendingAction !== undefined}
              loading={pendingAction === 'revise'}
              onClick={() => void run(() => planning.actions.revise(nextRevisionPrompt), 'revise')}
            >
              Revise
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

EstablishedAhpSessionPlan.displayName = 'EstablishedAhpSessionPlan'
